import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MapPin, Calculator } from 'lucide-react';

const MarkvardeKalkylator = () => {
  const [arligAvkastning, setArligAvkastning] = useState(5000);
  const [ranta, setRanta] = useState(3);
  const [result, setResult] = useState(null);

  // Faustmann formula for forest land value
  const calculateLandValue = () => {
    // Simple capitalization: V = R / r
    const markvarde = arligAvkastning / (ranta / 100);
    
    // Alternative scenarios
    const scenarios = [
      { ranta: ranta - 1, varde: arligAvkastning / ((ranta - 1) / 100) },
      { ranta: ranta, varde: markvarde },
      { ranta: ranta + 1, varde: arligAvkastning / ((ranta + 1) / 100) }
    ].filter(s => s.ranta > 0);
    
    setResult({
      markvarde: Math.round(markvarde),
      scenarios: scenarios.map(s => ({ ...s, varde: Math.round(s.varde) })),
      vardePerM3sk: Math.round(markvarde / 150) // Assuming ~150 m³sk typical standing volume
    });
  };

  return (
    <Card className="w-full" data-testid="markvarde-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <MapPin className="h-5 w-5" />
          Markvärde kalkylator
        </CardTitle>
        <CardDescription>
          Uppskatta värdet på skogsmark baserat på förväntad avkastning och 
          kalkylränta. Använd Faustmanns formel för markvärdesberäkning.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="avkastning">Årlig avkastning (kr/ha)</Label>
            <Input
              id="avkastning"
              type="number"
              value={arligAvkastning}
              onChange={(e) => setArligAvkastning(Number(e.target.value))}
              min={0}
              data-testid="avkastning-input"
            />
            <p className="text-xs text-muted-foreground">
              Nettointäkt från skogen per hektar och år
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="ranta">Kalkylränta (%)</Label>
            <Input
              id="ranta"
              type="number"
              step="0.5"
              value={ranta}
              onChange={(e) => setRanta(Number(e.target.value))}
              min={0.5}
              max={10}
              data-testid="ranta-input"
            />
            <p className="text-xs text-muted-foreground">
              Vanligt: 2-4% för skogsmark
            </p>
          </div>
        </div>

        <Button 
          onClick={calculateLandValue} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          Beräkna markvärde
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">Resultat</h4>
            <div className="text-center pb-4 border-b border-forest-200">
              <p className="text-sm text-muted-foreground">Uppskattat markvärde</p>
              <p className="text-3xl font-bold text-forest-700">{result.markvarde.toLocaleString('sv-SE')} kr/ha</p>
              <p className="text-sm text-muted-foreground mt-1">
                ca {result.vardePerM3sk.toLocaleString('sv-SE')} kr/m³sk
              </p>
            </div>
            
            {/* Sensitivity analysis */}
            <div className="mt-4">
              <p className="text-sm font-medium text-forest-800 mb-2">Känslighetsanalys</p>
              <div className="grid grid-cols-3 gap-2">
                {result.scenarios.map((scenario, index) => (
                  <div 
                    key={index} 
                    className={`p-2 rounded text-center ${
                      scenario.ranta === ranta 
                        ? 'bg-forest-100 border border-forest-300' 
                        : 'bg-gray-50'
                    }`}
                  >
                    <p className="text-xs text-muted-foreground">{scenario.ranta}% ränta</p>
                    <p className="font-semibold text-forest-700">{scenario.varde.toLocaleString('sv-SE')} kr</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          Markvärde baseras på förväntad framtida avkastning diskonterad till nuvärde. 
          Lägre ränta ger högre markvärde. Faustmanns formel används vid 
          skogsekonomiska beräkningar.
        </p>
      </CardContent>
    </Card>
  );
};

export default MarkvardeKalkylator;
